import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';

// The additional recipients only. The task's department and its assignee are not returned — the
// task row the popup opened on already carries both.
export interface TaskNotificationRecipient {
  userEmail: string;
  displayName: string;
  // False for someone the task's configuration names (#4624) — shown, but not this task's to remove.
  removable: boolean;
}

// A site task reports to the department person of every project on its site.
export interface TaskSiteDepartmentRecipient {
  /** #4994: null when the project's role is held by a whole department (departmentId/Name). */
  userEmail: string | null;
  departmentId?: number | null;
  departmentName?: string | null;
  displayName: string;
  projectId: number;
  projectName: string;
}

export interface TaskNotificationRecipients {
  recipients: TaskNotificationRecipient[];
  siteDepartmentRecipients: TaskSiteDepartmentRecipient[];
}

interface TaskNotificationRecipientsResult {
  hasError?: boolean;
  feedback?: string;
  recipients?: TaskNotificationRecipient[];
  siteDepartmentRecipients?: TaskSiteDepartmentRecipient[];
}

@Injectable({
  providedIn: 'root',
})
export class TaskNotificationsService {
  constructor(private apiService: ApiBaseService) {}

  // API soft-error envelope: rejections come back as HTTP 200 with { hasError: true, feedback },
  // not as a thrown HTTP error.
  private async unwrap(request: Promise<any>): Promise<TaskNotificationRecipients> {
    const response = (await request) as TaskNotificationRecipientsResult;

    if (response?.hasError) {
      throw new Error(
        response?.feedback || $localize`:@@taskNotificationsRequestFailed:The recipients could not be saved.`,
      );
    }

    return {
      recipients: response?.recipients ?? [],
      siteDepartmentRecipients: response?.siteDepartmentRecipients ?? [],
    };
  }

  async getRecipients(projectTaskId: number): Promise<TaskNotificationRecipients> {
    return this.unwrap(
      this.apiService.httpRequest(
        'NotificationCenterService/GetTaskRecipients',
        { ProjectTaskId: projectTaskId },
        true,
        false,
      ),
    );
  }

  async saveRecipients(projectTaskId: number, userEmails: string[]): Promise<TaskNotificationRecipients> {
    return this.unwrap(
      this.apiService.httpRequest(
        'NotificationCenterService/SaveTaskRecipients',
        { ProjectTaskId: projectTaskId, UserEmails: userEmails },
        true,
        false,
      ),
    );
  }
}
